import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  getLibraryItemStatusLabel,
  getLibrarySourceTypeLabel,
  libraryItemStatusOptions,
  librarySourceTypeOptions,
  type LibrarySearchResult,
  type LibrarySourceType,
} from "@/lib/library";
import { CitationStylePicker } from "@/components/settings/CitationStylePicker";
import { CoverSection } from "./libraryItemEditor/CoverSection";

type Draft = LibrarySearchResult & { notes?: string | null };

export const LibraryItemEditor = ({
  draft,
  onChange,
  idPrefix = "library",
  workspaceSection,
  libraryItemId,
  workspaceId = null,
}: {
  draft: Draft;
  onChange: Dispatch<SetStateAction<Draft>>;
  idPrefix?: string;
  /** Optional workspace membership / move controls injected by the host page. */
  workspaceSection?: ReactNode;
  /** ID of the saved library item — required to enable cover uploads. */
  libraryItemId?: string | null;
  /** Workspace context — required to scope the citation-style favorites/recents picker. */
  workspaceId?: string | null;
}) => {
  const updateDraft = (updater: (current: Draft) => Draft) => {
    onChange((current) => updater(current));
  };

  const centsToString = (cents: number | null | undefined) =>
    cents != null ? (cents / 100).toFixed(2) : "";

  const purchaseFocusedRef = useRef(false);
  const replacementFocusedRef = useRef(false);
  const [purchaseCostInput, setPurchaseCostInput] = useState(() => centsToString(draft.purchaseCostCents));
  const [replacementCostInput, setReplacementCostInput] = useState(() => centsToString(draft.replacementCostCents));

  useEffect(() => {
    if (!purchaseFocusedRef.current) setPurchaseCostInput(centsToString(draft.purchaseCostCents));
  }, [draft.purchaseCostCents]);
  useEffect(() => {
    if (!replacementFocusedRef.current) setReplacementCostInput(centsToString(draft.replacementCostCents));
  }, [draft.replacementCostCents]);

  const commitCost = (raw: string, key: "purchaseCostCents" | "replacementCostCents") => {
    const trimmed = raw.trim();
    const parsed = Number.parseFloat(trimmed);
    const cents = trimmed === "" || Number.isNaN(parsed) ? null : Math.round(Math.max(0, parsed) * 100);
    updateDraft((current) => ({ ...current, [key]: cents }));
    return cents;
  };

  const updateTextField = <K extends keyof Draft>(key: K, value: string) => {
    updateDraft((current) => ({ ...current, [key]: value || null }));
  };

  const updatePublishedDate = (value: string) => {
    const normalized = value.trim();
    const yearMatch = normalized.match(/^(\d{4})/);
    updateDraft((current) => ({
      ...current,
      publishedDate: normalized || null,
      publishedYear: yearMatch ? Number.parseInt(yearMatch[1], 10) : null,
    }));
  };

  const updatePageCount = (value: string) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    updateDraft((current) => ({
      ...current,
      pageCount: digitsOnly ? Number.parseInt(digitsOnly, 10) : null,
    }));
  };

  const updateAuthorText = (value: string) => {
    updateDraft((current) => ({
      ...current,
      authorText: value || null,
      authors: value.split(",").map((entry) => entry.trim()).filter(Boolean),
    }));
  };

  return (
    <div className="space-y-4">
    <CoverSection
      draft={draft}
      idPrefix={idPrefix}
      libraryItemId={libraryItemId}
      onUpdateDraft={updateDraft}
      onTextField={(key, value) => updateTextField(key as keyof Draft, value)}
    />
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2 space-y-2">
        <Label htmlFor={`${idPrefix}-source-type`}>Source type</Label>
        <Select
          value={draft.sourceType}
          onValueChange={(value) => updateDraft((current) => ({ ...current, sourceType: value as LibrarySourceType }))}
        >
          <SelectTrigger id={`${idPrefix}-source-type`}>
            <SelectValue placeholder="Choose source type" />
          </SelectTrigger>
          <SelectContent>
            {librarySourceTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{getLibrarySourceTypeLabel(draft.sourceType)} fields will appear below.</p>
      </div>
      <div className="sm:col-span-2 space-y-2">
        <Label htmlFor={`${idPrefix}-title`}>Title</Label>
          <Input id={`${idPrefix}-title`} value={draft.title} onChange={(e) => updateDraft((current) => ({ ...current, title: e.target.value }))} />
      </div>
      <div className="sm:col-span-2 space-y-2">
        <Label htmlFor={`${idPrefix}-subtitle`}>Subtitle</Label>
          <Input id={`${idPrefix}-subtitle`} value={draft.subtitle ?? ""} onChange={(e) => updateTextField("subtitle", e.target.value)} />
      </div>
      <div className="sm:col-span-2 space-y-2">
        <Label htmlFor={`${idPrefix}-display-title`}>Display title override</Label>
          <Input id={`${idPrefix}-display-title`} value={draft.displayTitle ?? ""} onChange={(e) => updateTextField("displayTitle", e.target.value)} />
      </div>
      <div className="sm:col-span-2 space-y-2">
        <Label htmlFor={`${idPrefix}-author-text`}>Author display text</Label>
          <Input id={`${idPrefix}-author-text`} value={draft.authorText ?? ""} onChange={(e) => updateAuthorText(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-publisher`}>Publisher</Label>
          <Input id={`${idPrefix}-publisher`} value={draft.publisher ?? ""} onChange={(e) => updateTextField("publisher", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-published-date`}>Published date</Label>
          <Input id={`${idPrefix}-published-date`} value={draft.publishedDate ?? ""} onChange={(e) => updatePublishedDate(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-language-code`}>Language code</Label>
          <Input id={`${idPrefix}-language-code`} value={draft.languageCode ?? ""} onChange={(e) => updateTextField("languageCode", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-page-count`}>Page count</Label>
          <Input id={`${idPrefix}-page-count`} inputMode="numeric" value={draft.pageCount?.toString() ?? ""} onChange={(e) => updatePageCount(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-status`}>Status</Label>
          <Select value={draft.status} onValueChange={(value) => updateDraft((current) => ({ ...current, status: value as typeof current.status }))}>
          <SelectTrigger id={`${idPrefix}-status`}>
            <SelectValue placeholder="Choose status" />
          </SelectTrigger>
          <SelectContent>
            {libraryItemStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-isbn13`}>ISBN-13</Label>
          <Input id={`${idPrefix}-isbn13`} value={draft.isbn13 ?? ""} onChange={(e) => updateTextField("isbn13", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-isbn10`}>ISBN-10</Label>
          <Input id={`${idPrefix}-isbn10`} value={draft.isbn10 ?? ""} onChange={(e) => updateTextField("isbn10", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-edition`}>Edition</Label>
          <Input id={`${idPrefix}-edition`} value={draft.edition ?? ""} onChange={(e) => updateTextField("edition", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-volume`}>Volume</Label>
          <Input id={`${idPrefix}-volume`} value={draft.volume ?? ""} onChange={(e) => updateTextField("volume", e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-series`}>Series</Label>
          <Input id={`${idPrefix}-series`} value={draft.series ?? ""} onChange={(e) => updateTextField("series", e.target.value)} />
      </div>
      {(draft.sourceType === "journal_article" || draft.sourceType === "chapter" || draft.sourceType === "proceedings_article") && (
        <div className="sm:col-span-2 space-y-2">
          <Label htmlFor={`${idPrefix}-container-title`}>
            {draft.sourceType === "chapter"
              ? "Book title (parent)"
              : draft.sourceType === "proceedings_article"
              ? "Conference / proceedings title"
              : "Journal title"}
          </Label>
          <Input
            id={`${idPrefix}-container-title`}
            value={draft.containerTitle ?? ""}
            onChange={(e) => updateTextField("containerTitle", e.target.value)}
          />
        </div>
      )}
      {(draft.sourceType === "journal_article" || draft.sourceType === "chapter" || draft.sourceType === "proceedings_article" || draft.sourceType === "report") && (
        <div className="sm:col-span-2 space-y-2">
          <Label htmlFor={`${idPrefix}-doi`}>DOI</Label>
          <Input
            id={`${idPrefix}-doi`}
            placeholder="10.xxxx/xxxxx"
            value={draft.doi ?? ""}
            onChange={(e) => updateTextField("doi", e.target.value)}
          />
        </div>
      )}
      {(draft.sourceType === "website" || draft.sourceType === "report" || draft.sourceType === "other") && (
        <>
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor={`${idPrefix}-url`}>URL</Label>
            <Input
              id={`${idPrefix}-url`}
              type="url"
              placeholder="https://…"
              value={draft.url ?? ""}
              onChange={(e) => updateTextField("url", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-accessed-date`}>Accessed date</Label>
            <Input
              id={`${idPrefix}-accessed-date`}
              type="date"
              value={draft.accessedDate ?? ""}
              onChange={(e) => updateTextField("accessedDate", e.target.value)}
            />
          </div>
        </>
      )}
    </div>
    {draft.sourceType === "book" && (
      <div className="space-y-3 rounded-md border p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-is-owned`} className="cursor-pointer">Own a physical copy</Label>
            <p className="text-xs text-muted-foreground">
              Mark this if the book is on your shelf. Used for future library manifest and valuation features.
            </p>
          </div>
          <Switch
            id={`${idPrefix}-is-owned`}
            checked={!!draft.isOwned}
            onCheckedChange={(checked) =>
              updateDraft((current) => ({
                ...current,
                isOwned: checked,
                ...(checked
                  ? { bookBinding: current.bookBinding ?? "paperback" }
                  : {}),
              }))
            }
          />
        </div>

        {draft.isOwned ? (
          <div className="space-y-3 border-t pt-3">
            {/* Binding + costs */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Binding</Label>
                <div className="inline-flex overflow-hidden rounded-md border">
                  {(["paperback", "hardback"] as const).map((opt) => {
                    const active = (draft.bookBinding ?? "paperback") === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateDraft((current) => ({ ...current, bookBinding: opt }))}
                        className={`px-3 py-1 text-xs font-semibold transition ${
                          active
                            ? "bg-primary text-primary-foreground"
                            : "bg-background text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {opt === "paperback" ? "Paperback" : "Hardback"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor={`${idPrefix}-purchase-cost`} className="text-sm">Purchase</Label>
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    id={`${idPrefix}-purchase-cost`}
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    className="h-8 w-28 text-right"
                    value={purchaseCostInput}
                    onFocus={() => { purchaseFocusedRef.current = true; }}
                    onChange={(e) => setPurchaseCostInput(e.target.value)}
                    onBlur={() => {
                      purchaseFocusedRef.current = false;
                      const cents = commitCost(purchaseCostInput, "purchaseCostCents");
                      setPurchaseCostInput(centsToString(cents));
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor={`${idPrefix}-replacement-cost`} className="text-sm">Replacement</Label>
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    id={`${idPrefix}-replacement-cost`}
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    className="h-8 w-28 text-right"
                    value={replacementCostInput}
                    onFocus={() => { replacementFocusedRef.current = true; }}
                    onChange={(e) => setReplacementCostInput(e.target.value)}
                    onBlur={() => {
                      replacementFocusedRef.current = false;
                      const cents = commitCost(replacementCostInput, "replacementCostCents");
                      setReplacementCostInput(centsToString(cents));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* First edition */}
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={`${idPrefix}-is-first-edition`} className="cursor-pointer text-sm">First edition</Label>
              <Switch
                id={`${idPrefix}-is-first-edition`}
                checked={!!draft.isFirstEdition}
                onCheckedChange={(checked) => updateDraft((current) => ({ ...current, isFirstEdition: checked }))}
              />
            </div>

            {/* Numbered + index/total */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor={`${idPrefix}-is-numbered`} className="cursor-pointer text-sm">Numbered</Label>
                <Switch
                  id={`${idPrefix}-is-numbered`}
                  checked={!!draft.isNumbered}
                  onCheckedChange={(checked) =>
                    updateDraft((current) => ({
                      ...current,
                      isNumbered: checked,
                      ...(checked ? {} : { numberedIndex: null, numberedTotal: null }),
                    }))
                  }
                />
              </div>
              {draft.isNumbered ? (
                <div className="flex items-center gap-2 pl-1">
                  <Label htmlFor={`${idPrefix}-numbered-index`} className="text-xs text-muted-foreground">Number</Label>
                  <Input
                    id={`${idPrefix}-numbered-index`}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    className="h-8 w-24"
                    value={draft.numberedIndex ?? ""}
                    onChange={(e) =>
                      updateDraft((current) => ({
                        ...current,
                        numberedIndex: e.target.value ? Math.max(1, Number.parseInt(e.target.value, 10) || 0) : null,
                      }))
                    }
                  />
                  <span className="text-xs text-muted-foreground">of</span>
                  <Input
                    id={`${idPrefix}-numbered-total`}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    className="h-8 w-24"
                    value={draft.numberedTotal ?? ""}
                    onChange={(e) =>
                      updateDraft((current) => ({
                        ...current,
                        numberedTotal: e.target.value ? Math.max(1, Number.parseInt(e.target.value, 10) || 0) : null,
                      }))
                    }
                  />
                  {draft.numberedIndex != null &&
                  draft.numberedTotal != null &&
                  draft.numberedIndex > draft.numberedTotal ? (
                    <span className="text-xs text-destructive">Number must be ≤ total</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Signed */}
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={`${idPrefix}-is-signed`} className="cursor-pointer text-sm">Signed</Label>
              <Switch
                id={`${idPrefix}-is-signed`}
                checked={!!draft.isSigned}
                onCheckedChange={(checked) => updateDraft((current) => ({ ...current, isSigned: checked }))}
              />
            </div>
          </div>
        ) : null}
      </div>
    )}
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-description`}>
        Synopsis{" "}
        <span className="text-xs font-normal text-muted-foreground">(Text may be truncated at 1,000 characters.)</span>
      </Label>
        <Textarea id={`${idPrefix}-description`} rows={6} value={draft.description ?? ""} onChange={(e) => updateTextField("description", e.target.value)} />
    </div>
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-notes`}>Notes</Label>
        <Textarea id={`${idPrefix}-notes`} rows={4} value={draft.notes ?? ""} onChange={(e) => updateTextField("notes", e.target.value)} />
    </div>
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-citation-style-override`}>Citation style override</Label>
      <CitationStylePicker
        value={draft.citationStyleOverride ?? "__workspace_default__"}
        onChange={(next) =>
          updateDraft((current) => ({
            ...current,
            citationStyleOverride: next === "__workspace_default__" || next === "" ? null : next,
          }))
        }
        workspaceId={workspaceId}
        triggerClassName="w-full justify-between"
        extraTopOption={{ value: "__workspace_default__", label: "Workspace default" }}
      />
      <p className="text-xs text-muted-foreground">
        When set, citations referencing this source render in the chosen style regardless of the workspace default. Searches the
        full CSL Style Repository (~10,000 styles).
      </p>
    </div>
      {workspaceSection}
      <p className="text-xs text-muted-foreground">Current workflow status: {getLibraryItemStatusLabel(draft.status)}.</p>
    </div>
  );
};
